import angular from 'angular';
import VideoPlayer from '../videoPlayer/VideoPlayer';
import _ from 'lodash';
// html for the different flavors
import alertTpl from './templates/alert.html';
import confirmTpl from './templates/confirm.html';
import iframeTpl from './templates/iframe.html';
import promptTpl from './templates/prompt.html';
import stateTpl from './templates/state.html';
import textTpl from './templates/text.html';
import videoTpl from './templates/video.html';

/**
 * Create and manage modal windows
 */
export default class modalService {
	/**
	 * Get injections
	 */
	constructor($compile, $rootScope, $timeout, $state, $resolve, $injector, Fullscreen, EventTracker) {
		'ngInject';
		// save injections
		/** @private */
		this.$state = $state;
		/** @private */
		this.$rootScope = $rootScope;
		/** @private */
		this.$compile = $compile;
		/** @private */
		this.$resolve = $resolve;
		/** @private */
		this.$injector = $injector;
		/** @private */
		this.$timeout = $timeout;
		/** @private */
		this.Fullscreen = Fullscreen;
		/** @private */
		this.EventTracker = EventTracker;
		/** @private */
		this.modals = [];
	}

	/**
	 * Get the top-most modal object or undefined if no modals are open
	 */
	getTop() {
		return this.modals[this.modals.length - 1];
	}

	/**
	 * Attempt to close the top-most modal
	 */
	closeTop() {
		const top = this.getTop();
		if (top) {
			top.close();
		}
	}

	/**
	 * Attempt to close all modals
	 */
	closeAll(title = null) {
		this.modals.forEach(modal => {
			if (!title || title == modal.$scope.vm.heading) {
				modal.close();
			}
		});
	}

	/**
	 * Resolve open modal
	 */
	resolveByTitle(title = null, msg = 'modal resolved') {
		if (!title) {
			return false;
		}
		this.modals.forEach(modal => {
			if (title == modal.$scope.vm.heading) {
				modal.yes(msg);
			}
		});
		return true;
	}

	/**
	 * Open a modal with the given template. Used internally.
	 * @param {String} template  The angular template to $compile
	 * @param {Object} vm  Values to define on the view model
	 * @returns {Promise}
	 */
	openWithTemplate(template, vm = {}) {
		vm.modalBody = vm.escapedHtml || vm.text || '';
		// create the actual element
		const $scope = this.$rootScope.$new();
		const element = angular.element(template);
		const compiled = this.$compile(element)($scope);
		angular.element(document.body).append(compiled);
		// create some helper functions
		const cleanup = () => {
			$scope.$destroy();
			const idx = this.modals.indexOf(promise);
			if (idx > -1) {
				this.modals.splice(idx, 1);
			}
		};
		const promise = new Promise((resolve, reject) => {
			let resolvers = {
				isOpen: true,
				yes: resolveWith => {
					vm.isOpen = false;
					setTimeout(cleanup, 515); // after 500ms animation
					this.$timeout(() => resolve(resolveWith));
				},
				cancel: rejectWith => {
					vm.isOpen = false;
					setTimeout(cleanup, 515); // after 500ms animation
					this.$timeout(() => reject(rejectWith));
				},
			};
			_.extend(vm, resolvers);
			$scope.vm = vm;
			if (vm.timeoutMs > 0) {
				setTimeout(() => resolvers.cancel('timeout'), vm.timeoutMs);
			}
			this.$timeout(() => {});
		});
		this.modals.push(promise);
		// add some properties to promise
		promise.$scope = $scope;
		promise.yes = vm.yes;
		promise.close = vm.cancel;
		promise.element = compiled;
		promise.focus = this.attemptFocus;
		promise.origin = 'modalService';
		promise.updateText = function (newText) {
			$scope.vm.text = newText;

			return promise;
		};
		promise.updateHtmlBody = function (escapedHtml) {
			$scope.vm.escapedHtml = escapedHtml;

			return promise;
		};
		return promise;
	}

	/**
	 * Attempt to focus on the top-most modal window
	 */
	attemptFocus() {
		setTimeout(() => {
			try {
				const modals = document.querySelectorAll('.sh-modal-body');
				const input = modals[modals.length - 1].querySelector('.form-input');
				input.select();
				input.focus();
			} catch (e) {}
		}, 100);
	}

	/**
	 * Create an alert box
	 * @param {Object|String} options  Object with options or use a string to specify "text" only
	 * @property {String} text 				optional text to show as modal body
	 * @property {String} escapedHtml 		optional html string to show as modal body
	 * @property {String} [heading=Notice]  The heading text to use
	 * @property {String} [confirmText=OK]  The button text
	 * @property {Number} [width=auto]      Width in pixels
	 * @property {Number} [height=auto]     Height in pixels
	 * @property {Number} [timeoutMs]       Number of milliseconds after which to auto close
	 * @return {Promise}  Resolves when user clicks "OK", rejected if user clicks close
	 */
	alert(options = {}) {
		const modalOptions = {
			heading: 'Notice',
			width: 'auto',
			height: 'auto',
			...options,
		};

		return this.openWithTemplate(alertTpl, modalOptions);
	}

	/**
	 * Create an confirm box
	 * @param {Object|String} options  Object with options or use a string to specify "text" only
	 * @property {String} text 						optional text to show as modal body
	 * @property {String} escapedHtml 				optional html string to show as modal body
	 * @property {String} [heading=Please Confirm]  The modal heading text to use
	 * @property {String} [confirmText=Yes]         The confirm button text
	 * @property {String} [cancelText=Cancel]       The cancel button text
	 * @property {Number} [width=auto]              Width in pixels
	 * @property {Number} [height=auto]             Height in pixels
	 * @property {Number} [timeoutMs]               Number of milliseconds after which to auto close
	 * @return {Promise}  Resolves when user clicks confirm, rejected if user clicks cancel or close
	 */
	confirm(options = {}) {
		const modalOptions = {
			heading: 'Please confirm',
			width: 'auto',
			height: 'auto',
			...options,
		};

		return this.openWithTemplate(confirmTpl, modalOptions);
	}

	/**
	 * Create an prompt
	 * @param {Object} options
	 * @property {String} text 						optional text to show as modal body
	 * @property {String} escapedHtml 				optional html string to show as modal body
	 * @property {String} model                    	The model to which to bind the response
	 * @property {String} [heading=Please specify]  The modal heading text to use
	 * @property {String} [confirmText=Submit]      The submit button text
	 * @property {String} [cancelText=Cancel]       The cancel button text
	 * @property {String} [inputText=""]            The starting text for the prompt
	 * @property {Number} [width=auto]              Width in pixels
	 * @property {Number} [height=auto]             Height in pixels
	 * @property {Number} [isPassword=false]        Should this show a `password` input type instead of a textarea
	 * @property {Boolean} [submitOnEntery=false]   Should input submit when enter is pressed
	 * @property {Number} [timeoutMs]               Number of milliseconds after which to auto close
	 * @return {Promise}  Resolves when user clicks submit, rejected if user clicks cancel or close
	 */
	prompt(options = {}) {
		const modalOptions = {
			heading: 'Please specify',
			width: 'auto',
			height: 'auto',
			isPassword: false,
			submitOnEnter: false,
			...options,
		};

		if (options.submitOnEnter) {
			options.checkForEnterKey = evt => {
				if (options.submitOnEnter && evt.keyCode == 13) {
					evt.preventDefault();
					options.yes(options.inputText);
				}
			};
		}

		const promise = this.openWithTemplate(promptTpl, modalOptions);
		this.attemptFocus();

		return promise;
	}

	/**
	 * Create an alert box with no close button
	 * @param {Object|String} options
	 * @property {String} text 				optional text to show as modal body
	 * @property {String} escapedHtml 		optional html string to show as modal body
	 * @property {String} [heading=Info]  	The modal heading text to use
	 * @property {Number} [width=auto]    	Width in pixels
	 * @property {Number} [height=auto]   	Height in pixels
	 * @property {Number} [timeoutMs]     	Number of milliseconds after which to auto close
	 * @return {Promise}  Resolves when user clicks X
	 */
	text(options = {}) {
		const modalOptions = {
			heading: 'Info',
			width: 'auto',
			height: 'auto',
			...options,
		};

		return this.openWithTemplate(textTpl, modalOptions);
	}

	/**
	 * Create an alert box with no close button
	 * @param {Object|String} options
	 * @property {String} text 				optional text to show as modal body
	 * @property {String} escapedHtml 		optional html string to show as modal body
	 * @property {String} [heading=Info]  	The modal heading text to use
	 * @property {Number} [width=auto]    	Width in pixels
	 * @property {Number} [height=auto]   	Height in pixels
	 * @property {Number} [timeoutMs]     	Number of milliseconds after which to auto close
	 * @return {Promise}  Resolves when user clicks X
	 */
	html(options = {}) {
		const modalOptions = {
			heading: 'Info',
			width: 'auto',
			height: 'auto',
			...options,
		};

		return this.openWithTemplate(textTpl, modalOptions);
	}

	/**
	 * Create a modal containing just an iframe
	 * @param {String} url
	 * @param {Object} [options]
	 * @property {String} [heading=Info]  The modal heading text to use
	 * @property {Number} [width=auto]    Width in pixels
	 * @property {Number} [height=auto]   Height in pixels
	 * @property {Number} [timeoutMs]     Number of milliseconds after which to auto close
	 * @return {Promise}  Resolves when user clicks X
	 */
	iframe(url, options = {}) {
		const iframeId = 'iframe' + +new Date();
		options = _.extend(
			{
				heading: 'Info',
				width: 'full',
				height: 'full',
				iframeUrl: url,
				iframeId,
			},
			options
		);
		const promise = this.openWithTemplate(iframeTpl, options);
		promise.listen = callback => {
			setTimeout(() => {
				const source = document.getElementById(options.iframeId).contentWindow;
				const handler = evt => {
					if (evt.source === source) {
						callback(evt);
					}
				};
				const removeHandler = () => window.removeEventListener('message', handler);
				window.addEventListener('message', handler);
				promise.then(removeHandler, removeHandler);
			}, 10);
		};
		return promise;
	}

	/**
	 * Create a modal to play the given video
	 * @param {String} url
	 * @param {Object} [options]
	 * @property {String} [heading=Video]  The modal heading text to use
	 * @property {Number} [width=auto]     Width in pixels
	 * @property {Number} [height=auto]    Height in pixels
	 * @property {Number} [timeoutMs]      Number of milliseconds after which to auto close
	 * @return {Promise}  Resolves when user clicks confirm, rejected if user clicks cancel or close
	 */
	video(url, options = {}) {
		options = _.extend(
			{
				heading: 'Video',
				width: 'auto',
				height: 'auto',
			},
			options
		);
		if (typeof url == 'object') {
			url = VideoPlayer.getPlayableUrl(url, options);
		}
		const promise = this.openWithTemplate(videoTpl, options);
		this.$timeout(() => {
			// modal is in DOM
			setTimeout(() => {
				// digest has run
				// set up video player
				let wrapper = promise.element[0].querySelector('.sh-modal-video-wrapper');
				let div = document.createElement('div');
				wrapper.appendChild(div);
				let player = new VideoPlayer(div, this);
				player.play(url);
				this.EventTracker.save('start_video', {
					url,
					type: player.type,
					heading: options.heading,
				});
				const onClose = () => {
					player.stop();
					wrapper.removeChild(div);
					this.EventTracker.save('stop_video', {
						url,
						type: player.type,
						heading: options.heading,
					});
				};
				promise.then(onClose, onClose);
			}, 0);
		});
		return promise;
	}

	/**
	 * Open the given controller state in a modal
	 * @param {String} stateName  The name registered with ui-router such as hub.loggedin.PostEdit
	 * @param {Object} [options]
	 * @property {Object} [params]        Parameters to pass to the ui-router state
	 * @property {String} [heading=Info]  The modal heading text to use
	 * @property {Number} [width=auto]    Width in pixels
	 * @property {Number} [height=auto]   Height in pixels
	 * @property {Number} [timeoutMs]     Number of milliseconds after which to auto close
	 * @return {Promise}  Can be resolved from child route or rejected if user clicks cancel or close
	 */
	state(stateName, options = {}) {
		const state = this.$state.get(stateName);
		if (!state) {
			throw new Error(`ModalService: state "${stateName}" not found.`);
		}
		const stateSlug = stateName.replace(/\W+/g, '-');
		const shortStateSlug = stateName.split('.').pop();
		options = _.extend(
			{
				heading: 'Info',
				width: 'full',
				height: 'full',
				modalClass: `sh-modal-${stateSlug} sh-modal-${shortStateSlug} state-${stateSlug} state-${shortStateSlug}`,
				padding: true,
				locals: {},
				injections: {},
			},
			options
		);
		const promise = this.openWithTemplate(stateTpl, options);
		const load = () =>
			new Promise((onLoadResolve, onLoadReject) => {
				const $stateParams = options.params || {};
				const resolves = {
					...state.resolve,
					__templateHtml: this.getTemplateResolver(state, $stateParams),
				};
				const stateChangeError = 'Changing modal state is not allowed. Please use a second modal window.';
				const controllerInjections = {
					...options.injections,
					$stateParams,
					$state: {
						// see https://ui-router.github.io/ng1/docs/0.4.2/index.html#/api/ui.router.state.$state
						get: this.$state.get.bind(this.$state),
						href: this.$state.href.bind(this.$state),
						includes: this.$state.includes.bind(this.$state),
						is: this.$state.is.bind(this.$state),
						reload: load,
						go: () => {
							throw new Error(stateChangeError);
						},
						transitionTo: () => {
							throw new Error(stateChangeError);
						},
						params: $stateParams,
						current: state,
						transition: Promise.resolve(true),
					},
					modalState: state,
					modalOptions: options,
					modalInstance: promise,
					modalService: this,
				};
				this.$resolve.resolve(resolves, controllerInjections).then(resolved => {
					const modalBody = promise.element[0].querySelector('.sh-modal-body');
					const $scope = promise.$scope.$new();
					resolved.$scope = $scope;
					// resolve
					const controller = this.$injector.instantiate(state.controller, resolved);
					const vm = state.controllerAs;
					$scope[vm] = controller;
					_.extend(controller, options.locals);
					controller.$resolve = promise.yes;
					controller.$reject = promise.close;
					controller.onModalReady && this.$timeout(() => controller.onModalReady());
					promise.$scope.vm.onBeforeModalExit = () => {
						if (_.isFunction(controller.onBeforeModalExit)) {
							return controller.onBeforeModalExit();
						}
						return Promise.resolve();
					};
					const element = angular.element(`<div class="sh-modal-state-wrapper">${resolved.__templateHtml}</div>`);
					const compiled = this.$compile(element)($scope);
					angular.element(modalBody).append(compiled);
					onLoadResolve(element);
					if (options.onAfterInit) {
						this.$timeout(() => {
							options.onAfterInit(controller, this);
						});
					}
					if (options.attemptFocus) {
						setTimeout(() => this.attemptFocus(), 100);
					}
					this._trackPageView({ state, options });
				}, onLoadReject);
			});
		promise.onLoad = load();
		return promise;
	}

	/**
	 * Track a page view (of a modal page)
	 * @param {Object} values
	 * @property {Object} state  The ui-router state
	 * @property {Object} options  The options passed to ModalService.state()
	 * @private
	 */
	_trackPageView({ state, options }) {
		const urlTemplate = `${window.BASE_URL}/hub#${state.url}`;
		const url = urlTemplate.replace(/:([\w_]+)/g, ($0, $1) => {
			return options.params[$1];
		});
		this.EventTracker.trackPageView({
			url,
			route: state.url,
			title: options.heading,
			referrer: window.location.href,
			isModal: true,
		});
	}

	/**
	 * Get a resolver function for the template URL
	 * @param {Object} state  The configuration object specified as the second parameter to $stateProvider.state()
	 * @property {String|Function} [templateUrl]  The URL (or function which returns a URL) from which to fetch HTML
	 * @property {String|Function} [template]  The HTML (or function which returns HTML)
	 * @property {Function} [templateProvider]  An injectable function that returns a promise that resolves with HTML
	 * @param {Object} $stateParams  Contains the params to pass to the state URL
	 * @returns {Function}
	 */
	getTemplateResolver(state, $stateParams) {
		if (typeof state.templateUrl == 'string') {
			return $templateCache => {
				'ngInject';
				const html = $templateCache.get(state.templateUrl);
				if (html) {
					return html;
				}
				return fetch(state.templateUrl)
					.then(res => res.text())
					.then(text => {
						$templateCache.put(state.templateUrl, text);
						return text;
					});
			};
		} else if (typeof state.templateUrl == 'function') {
			return () => fetch(state.templateUrl($stateParams)).then(res => res.text());
		} else if (typeof state.template == 'string') {
			return () => state.template;
		} else if (typeof state.template == 'function') {
			return state.template;
		} else if (state.templateProvider) {
			return state.templateProvider;
		} else {
			throw new Error('modalService.state() requires template, templateUrl or templateProvider');
		}
	}

	/**
	 * Go full screen when user confirms
	 * @param {String} [textToUse="Would you like to go full screen?"]  The confirmation text or HTML
	 * @param {Object|String} options  Object with options or use a string to specify "text" only
	 * @property {String} [heading=Would you like to go full screen?]  The modal heading text to use
	 * @property {String} [confirmText=Yes]         The confirm button text
	 * @property {String} [cancelText=Cancel]       The cancel button text
	 * @property {Number} [width=auto]              Width in pixels
	 * @property {Number} [height=auto]             Height in pixels
	 * @property {Number} [timeoutMs]               Number of milliseconds after which to auto close
	 * @return {Promise}  Resolves when user clicks confirm, rejected if user clicks cancel or close
	 */
	fullscreen(options = {}) {
		if (this.Fullscreen.element() == document.documentElement) {
			// already full screen on document element
			return;
		}
		// exit full screen of any other element
		this.Fullscreen.exit();

		options.text = options.text || 'Would you like to go full screen?';
		return this.confirm(options).then(() => {
			this.Fullscreen.request();
			return true;
		});
	}
}
